import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  IconButton,
  Menu,
  MenuItem,
  Typography,
  useTheme,
  TextField,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import SearchIcon from "@mui/icons-material/Search";
import CancelIcon from "@mui/icons-material/Cancel";
import { withTranslation } from "react-i18next";

import debounceFn from "utils/debounce";
import Tabs from "Components/Common/Tabs";
import Notifications from "./Notifications";

import {
  useDeleteNotificationMutation,
  useLazyNotificationQuery,
  useUpdateNotificationMutation,
} from "apis/Notification";

import bellIcon from "assets/images/svg/bell.svg";
import darkThemeLoadingGIF from "assets/images/loading-dark.gif";
import loadingGIF from "assets/images/loading.gif";

import {
  CheckedIcon,
  DeleteIcon,
  Loader,
  NotificationHeaderWrapper,
} from "./style";

const NotificationDrawer = ({
  handleDrawerClose,
  unreadNotificationCount,
  t,
}) => {
  const theme = useTheme();
  const [menuElement, setMenuElement] = useState();
  const [activeTab, setActiveTab] = React.useState(0);
  const [searchedText, setSearchedText] = useState("");

  const tabs = useMemo(
    () => ["All", `${t("Unread")} (${unreadNotificationCount ?? 0})`],
    [unreadNotificationCount, t]
  );

  const [updateNotification] = useUpdateNotificationMutation();
  const [deleteAllNotifications] = useDeleteNotificationMutation();

  const [
    getNotification,
    { isLoading, isFetching, data: { data: notifications } = {} },
  ] = useLazyNotificationQuery();

  const handleMarkAllReadAPI = async () => {
    try {
      await updateNotification(
        null,
        {},
        window.location.href.includes("fire-safety")
      );
    } catch (error) {}
  };

  const handleDeleteNotifications = async () => {
    try {
      await deleteAllNotifications(
        window.location.href.includes("fire-safety")
      );
    } catch (error) {}
  };

  const getNotifications = async (searchText) => {
    try {
      await getNotification(
        {
          isRead: activeTab === 0 ? "" : false,
          searchNotification: searchText,
        },
        false,
        window.location.href.includes("fire-safety")
      );
    } catch (error) {
      console.error(error);
    }
  };

  const handleChange = (e) => {
    setSearchedText(e.target.value);
    debounceFn(() => getNotifications(e.target.value), 1000);
  };

  useEffect(() => {
    getNotifications(searchedText);
  }, [unreadNotificationCount, activeTab]);

  return (
    <>
      <div>
        <NotificationHeaderWrapper>
          <div className="d-flex ">
            <CloseIcon onClick={handleDrawerClose} sx={{ cursor: "pointer" }} />
            <h4 className="ms-1">{t("Notifications")}</h4>
          </div>
          <div>
            <IconButton
              onClick={(event) => setMenuElement(event.currentTarget)}
            >
              <MoreHorizIcon />
            </IconButton>
            <Menu
              anchorEl={menuElement}
              open={!!menuElement}
              onClose={() => setMenuElement(null)}
            >
              <MenuItem
                onClick={handleMarkAllReadAPI}
                disabled={!unreadNotificationCount}
              >
                <CheckedIcon />
                <Typography className="ps-3" sx={{ fontSize: "14px" }}>
                  {t("Mark all as read")}
                </Typography>
              </MenuItem>

              <MenuItem
                onClick={handleDeleteNotifications}
                disabled={!notifications?.length}
              >
                <DeleteIcon />
                <Typography className="ps-3" sx={{ fontSize: "14px" }}>
                  {t("Delete All")}
                </Typography>
              </MenuItem>
            </Menu>
          </div>
        </NotificationHeaderWrapper>
        <Tabs
          tabs={tabs}
          values={activeTab}
          onChange={(_, tab) => {
            setActiveTab(tab);
          }}
        />
        <TextField
          fullWidth
          placeholder={t("Search notification")}
          id="fullWidth"
          size="small"
          className="mt-2"
          value={searchedText}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),

            endAdornment: searchedText && (
              <IconButton
                onClick={async () => {
                  await getNotifications("");
                  setSearchedText("");
                }}
              >
                <CancelIcon />
              </IconButton>
            ),
          }}
          onChange={handleChange}
        />
      </div>
      <div style={{ flex: 1, overflow: "auto" }} id="scrollableContainer">
        {tabs.map(
          (tab, index) =>
            activeTab === index && (
              <div key={tab}>
                {isFetching || isLoading ? (
                  <Loader
                    src={
                      theme.palette.mode === "dark"
                        ? darkThemeLoadingGIF
                        : loadingGIF
                    }
                    alt="Loading-Gif"
                  />
                ) : notifications?.length ? (
                  <Notifications
                    notifications={notifications}
                    handleDrawerClose={handleDrawerClose}
                  />
                ) : (
                  <>
                    <div className="w-25 w-sm-50 pt-3 mx-auto">
                      <img
                        src={bellIcon}
                        className="img-fluid"
                        alt="user-pic"
                      />
                    </div>
                    <div className="text-center pb-5 mt-2">
                      <h6 className="fs-18 fw-semibold lh-base">
                        {searchedText
                          ? t("Sorry! No search result found")
                          : t("Hey! You don't have any notifications")}
                      </h6>
                    </div>
                  </>
                )}
              </div>
            )
        )}
      </div>
    </>
  );
};

export default withTranslation()(NotificationDrawer);

NotificationDrawer.propTypes = {
  handleDrawerClose: PropTypes.func,
  unreadNotificationCount: PropTypes.number,
  t: PropTypes.func,
};
